export const PROFILE_IMAGE_FOLDER = 'enrollmentUserImages'
export const PROFILE_IMAGE_PATH_REGEX = /^enrollmentUserImages\/[0-9a-f]{64}\.jpg$/

export const PROFILE_IMAGE_MIN_SHORTER_SIDE_PX = 200
export const PROFILE_IMAGE_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024
export const PROFILE_IMAGE_OUTPUT_SIZE_PX = 512
export const PROFILE_IMAGE_JPEG_QUALITY = 0.85
export const PROFILE_IMAGE_CACHE_CONTROL = 'private, max-age=0, no-cache'

export enum ProfileImageAcceptedMimeType {
  Jpeg = 'image/jpeg',
  Png = 'image/png',
  Gif = 'image/gif',
  WebP = 'image/webp'
}

export const PROFILE_IMAGE_ACCEPTED_MIME_TYPES: ProfileImageAcceptedMimeType[] = [
  ProfileImageAcceptedMimeType.Jpeg,
  ProfileImageAcceptedMimeType.Png,
  ProfileImageAcceptedMimeType.Gif,
  ProfileImageAcceptedMimeType.WebP
]

/** Value for `<input type="file" accept="…">` — source formats; stored object is always JPEG. */
export const PROFILE_IMAGE_FILE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp'

export type ProfileImageCropArea = {
  x: number
  y: number
  width: number
  height: number
}

export const createProfileImageKey = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

export const buildProfileImagePath = (key: string): string => `${PROFILE_IMAGE_FOLDER}/${key}.jpg`

export const isAcceptedProfileImageMimeType = (mimeType: string): boolean =>
  PROFILE_IMAGE_ACCEPTED_MIME_TYPES.includes(mimeType as ProfileImageAcceptedMimeType)

export const isProfileImageFileSizeValid = (sizeBytes: number): boolean =>
  sizeBytes > 0 && sizeBytes <= PROFILE_IMAGE_MAX_FILE_SIZE_BYTES

export const getProfileImageShorterSide = (width: number, height: number): number => Math.min(width, height)

export const isProfileImageDimensionValid = (width: number, height: number): boolean =>
  getProfileImageShorterSide(width, height) >= PROFILE_IMAGE_MIN_SHORTER_SIDE_PX

export const isProfileImagePathValid = (path: string): boolean => PROFILE_IMAGE_PATH_REGEX.test(path)

export const getProfileImageUploadMetadata = (): {
  cacheControl: string
  contentType: ProfileImageAcceptedMimeType.Jpeg
} => ({
  cacheControl: PROFILE_IMAGE_CACHE_CONTROL,
  contentType: ProfileImageAcceptedMimeType.Jpeg
})

/** Draws the cropped region onto a 512×512 canvas and returns a JPEG blob. */
export const encodeProfileImageCrop = (source: CanvasImageSource, crop: ProfileImageCropArea): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = PROFILE_IMAGE_OUTPUT_SIZE_PX
    canvas.height = PROFILE_IMAGE_OUTPUT_SIZE_PX

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      reject(new Error('Canvas 2D context unavailable'))
      return
    }

    ctx.drawImage(
      source,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      PROFILE_IMAGE_OUTPUT_SIZE_PX,
      PROFILE_IMAGE_OUTPUT_SIZE_PX
    )

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to encode profile image'))
          return
        }
        resolve(blob)
      },
      ProfileImageAcceptedMimeType.Jpeg,
      PROFILE_IMAGE_JPEG_QUALITY
    )
  })
